import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RowActions } from "@/components/dashboard/RowActions";
import WhatsAppLink from "@/components/dashboard/WhatsAppLink";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import usePagination from "@/lib/usePagination";
import TablePagination from "@/components/dashboard/TablePagination";

export default function Users() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const load = async () => setRows((await api.get("/users")).data);
  useEffect(() => { load(); }, []);
  const filtered = rows.filter(u => !q || u.email?.toLowerCase().includes(q.toLowerCase()) || u.name?.toLowerCase().includes(q.toLowerCase()));
  const { page, setPage, totalPages, pageItems, rangeStart, rangeEnd, total } = usePagination(filtered, 20);
  const roleColor = { admin: "bg-navy text-gold hover:bg-navy", client: "bg-gold text-white hover:bg-gold", candidate: "border-navy/30 text-navy" };

  const changeRole = async (uid, role) => {
    try { await api.patch(`/users/${uid}`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (uid) => {
    try { await api.delete(`/users/${uid}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-8" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow text-navy/50">Admin</div>
          <h1 className="serif-display text-4xl mt-2">Signed-up users</h1>
          <p className="text-navy/60 mt-2">{rows.length} accounts</p>
        </div>
        <Input placeholder="Search name or email" value={q} onChange={(e)=>setQ(e.target.value)} className="max-w-xs" data-testid="users-search"/>
      </div>
      <div className="bg-white border border-navy/10">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-navy/50 py-8">No users match.</TableCell></TableRow>}
            {pageItems.map((u) => (
              <TableRow key={u.user_id}>
                <TableCell className="flex items-center gap-3">
                  {u.picture ? <img src={u.picture} className="w-8 h-8 rounded-full object-cover"/> : <div className="w-8 h-8 bg-navy text-gold flex items-center justify-center serif-display text-sm">{u.name?.[0] || "?"}</div>}
                  <span className="serif-display">{u.name}</span>
                </TableCell>
                <TableCell className="text-navy/80">{u.email}</TableCell>
                <TableCell>
                  {u.phone ? (
                    <div className="flex flex-col gap-0.5">
                      <span className="text-navy/70 text-sm">{u.phone}</span>
                      <WhatsAppLink
                        phone={u.phone}
                        message={`Hi ${(u.name || "").split(" ")[0] || "there"}, this is the Ravemora team.`}
                        testid={`whatsapp-user-${u.user_id}`}
                      />
                    </div>
                  ) : (
                    <span className="text-navy/30 text-sm">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v)} disabled={u.user_id === me?.user_id}>
                    <SelectTrigger className="w-32 h-8" data-testid={`role-${u.user_id}`}><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="candidate">candidate</SelectItem>
                      <SelectItem value="client">client</SelectItem>
                      <SelectItem value="admin">admin</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-navy/60 text-sm">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell>
                  {u.user_id !== me?.user_id && (
                    <RowActions
                      onDelete={() => del(u.user_id)}
                      deleteTestid={`delete-user-${u.user_id}`}
                      itemLabel={`${u.email}`}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination page={page} totalPages={totalPages} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} onPrev={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => Math.min(totalPages, p + 1))} testidPrefix="users-table" />
      </div>
    </div>
  );
}
